import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  Output,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { GlobalLanguageService } from '@shared/services/global-language.service';
import { IDropdownSettings, ListItem } from './multi-select.model';

export const DROPDOWN_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => MultiSelectComponent),
  multi: true,
};
const noop = () => {};

@Component({
  selector: 'app-multi-select',
  templateUrl: './multi-select.component.html',
  styleUrls: ['./multi-select.component.scss'],
  providers: [DROPDOWN_CONTROL_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiSelectComponent implements ControlValueAccessor {
  public _data: Array<ListItem> = [];
  public selectedItems: Array<ListItem> = [];
  public isDropdownOpen = true;
  _placeholder = 'Select';
  private _sourceDataType = null;
  private _sourceDataFields: Array<string> = [];
  filter: ListItem = new ListItem(this.data);
  private _itemsShowLimit = 3;

  defaultSettings: IDropdownSettings = {
    singleSelection: false,
    idField: 'field',
    textField: 'key',
    disabledField: 'isDisabled',
    enableCheckAll: true,
    selectAllText: 'Select All',
    unSelectAllText: 'UnSelect All',
    allowSearchFilter: true,
    allowUnSelectAll: false,
    limitSelection: -1,
    clearSearchFilter: true,
    maxHeight: 197,
    itemsShowLimit: 3,
    searchPlaceholderText: 'Search',
    noDataAvailablePlaceholderText: 'No data available',
    closeDropDownOnSelection: false,
    showSelectedItemsAtTop: false,
    defaultOpen: false,
    allowRemoteDataSearch: false,
  };
  public _settings = this.defaultSettings;

  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: any) => void = noop;

  @Input()
  public set placeholder(value: string) {
    if (value) {
      this._placeholder = value;
    } else {
      this._placeholder = 'Select';
    }
  }
  @Input()
  disabled = false;

  @Input()
  public set settings(value: IDropdownSettings) {
    if (value) {
      this._settings = Object.assign(this.defaultSettings, value);
    } else {
      this._settings = Object.assign(this.defaultSettings);
    }
  }

  @Input()
  public set data(value: Array<any>) {
    if (!value) {
      this._data = [];
    } else {
      const firstItem = value[0];
      this._sourceDataType = typeof firstItem;
      this._sourceDataFields = this.getFields(firstItem);
      this._data = value.map((item: any) =>
        typeof item === 'string' || typeof item === 'number'
          ? new ListItem(item)
          : new ListItem({
              id: item[this._settings.idField],
              text: item[this._settings.textField],
              isDisabled: item[this._settings.disabledField],
            })
      );
    }
  }

  @Input()
  set itemsShowLimit(value: number) {
    this._itemsShowLimit = value ?? 3;
    this._settings.itemsShowLimit = this._itemsShowLimit;
  }

  @Output() filterChange = new EventEmitter<any>();

  @Output() dropDownClose = new EventEmitter<any>();

  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() select = new EventEmitter<any>();

  @Output() deSelect = new EventEmitter<any>();

  @Output() selectAll = new EventEmitter<Array<any>>();

  @Output() deSelectAll = new EventEmitter<Array<any>>();

  @Output() sortedColumns = new EventEmitter();

  drop(params: CdkDragDrop<string[]>): void {
    moveItemInArray(this._data, params.previousIndex, params.currentIndex);
    this.sortedColumns.emit(this._data);
  }

  onFilterTextChange($event) {
    this.filterChange.emit($event);
  }

  constructor(private globalTranslate: GlobalLanguageService) {
    this._placeholder = this.globalTranslate.getTranslation('MULTISELECT.SELECT');
    this.defaultSettings.selectAllText =
      this.globalTranslate.getTranslation('MULTISELECT.SELECTALL');
    this.defaultSettings.unSelectAllText =
      this.globalTranslate.getTranslation('MULTISELECT.DESELECTALL');
    this.defaultSettings.searchPlaceholderText =
      this.globalTranslate.getTranslation('SHARED.SEARCH');
    this.defaultSettings.noDataAvailablePlaceholderText =
      this.globalTranslate.getTranslation('MULTISELECT.NODATA');
  }

  onItemClick(_$event: any, item: ListItem): void | false {
    if (this.disabled || item.isDisabled) {
      return false;
    }

    const found = this.isSelected(item);
    const allowAdd =
      this._settings.limitSelection === -1 ||
      (this._settings.limitSelection > 0 &&
        this.selectedItems.length < this._settings.limitSelection);
    if (!found) {
      if (allowAdd) {
        this.addSelected(item);
      }
    } else {
      this.removeSelected(item);
    }
    if (this._settings.singleSelection && this._settings.closeDropDownOnSelection) {
      this.closeDropdown();
    }
  }

  writeValue(value: any) {
    if (value !== undefined && value !== null && value.length > 0) {
      if (this._settings.singleSelection) {
        try {
          if (value.length >= 1) {
            const firstItem = value[0];
            this.selectedItems = [
              typeof firstItem === 'string' || typeof firstItem === 'number'
                ? new ListItem(firstItem)
                : new ListItem({
                    id: firstItem[this._settings.idField],
                    text: firstItem[this._settings.textField],
                    isDisabled: firstItem[this._settings.disabledField],
                  }),
            ];
          }
        } catch (e: any) {
          return e.body.msg;
        }
      } else {
        const _data = value.map((item: any) =>
          typeof item === 'string' || typeof item === 'number'
            ? new ListItem(item)
            : new ListItem({
                id: item[this._settings.idField],
                text: item[this._settings.textField],
                isDisabled: item[this._settings.disabledField],
              })
        );
        if (this._settings.limitSelection > 0) {
          this.selectedItems = _data.splice(0, this._settings.limitSelection);
        } else {
          this.selectedItems = _data;
        }
      }
    } else {
      this.selectedItems = [];
    }
    this.onChangeCallback(value);
  }

  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  @HostListener('blur')
  public onTouched() {
    this.closeDropdown();
    this.onTouchedCallback();
  }

  trackByFn(item: any) {
    return item.id;
  }

  isSelected(clickedItem: ListItem) {
    let found = false;
    this.selectedItems.forEach((item) => {
      if (clickedItem.id === item.id) {
        found = true;
      }
    });
    return found;
  }

  isLimitSelectionReached(): boolean {
    return this._settings.limitSelection === this.selectedItems.length;
  }

  isAllItemsSelected(): boolean {
    const itemDisabledCount = this._data.filter((item) => item.isDisabled).length;
    if ((!this.data || this.data.length === 0) && this._settings.allowRemoteDataSearch) {
      return false;
    }
    return this._data.length === this.selectedItems.length + itemDisabledCount;
  }

  showButton(): boolean {
    if (!this._settings.singleSelection) {
      if (this._settings.limitSelection > 0) {
        return false;
      }
      return true;
    } else {
      return false;
    }
  }

  itemShowRemaining(): number {
    return this.selectedItems.length - this._settings.itemsShowLimit;
  }

  addSelected(item: ListItem) {
    if (this._settings.singleSelection) {
      this.selectedItems = [];
      this.selectedItems.push(item);
    } else {
      this.selectedItems.push(item);
    }
    this.onChangeCallback(this.emittedValue(this.selectedItems));
    this.select.emit(this.emittedValue(item));
  }

  removeSelected(itemSel: ListItem) {
    if (this._settings.allowUnSelectAll || this.selectedItems.length > 1) {
      this.selectedItems.forEach((item) => {
        if (itemSel.id === item.id) {
          this.selectedItems.splice(this.selectedItems.indexOf(item), 1);
        }
      });
      this.onChangeCallback(this.emittedValue(this.selectedItems));
      this.deSelect.emit(this.emittedValue(itemSel));
    }
  }

  emittedValue(val: any): any {
    const selected = [];
    if (Array.isArray(val)) {
      val.forEach((item) => {
        selected.push(this.objectify(item));
      });
    } else {
      if (val) {
        return this.objectify(val);
      }
    }
    return selected;
  }

  objectify(val: ListItem) {
    if (this._sourceDataType === 'object') {
      const obj = {};
      obj[this._settings.idField] = val.id;
      obj[this._settings.textField] = val.text;
      if (this._sourceDataFields.includes(this._settings.disabledField)) {
        obj[this._settings.disabledField] = val.isDisabled;
      }
      return obj;
    }
    if (this._sourceDataType === 'number') {
      return Number(val.id);
    } else {
      return val.text;
    }
  }

  toggleDropdown(evt) {
    evt.preventDefault();
    if (this.disabled && this._settings.singleSelection) {
      return;
    }
    this._settings.defaultOpen = !this._settings.defaultOpen;
    if (!this._settings.defaultOpen) {
      this.dropDownClose.emit();
    }
  }

  closeDropdown() {
    this._settings.defaultOpen = false;
    if (this._settings.clearSearchFilter) {
      this.filter.text = '';
    }
    this.dropDownClose.emit();
  }

  toggleSelectAll(): void | false {
    const isAllItemsSelected = this.isAllItemsSelected();
    if (this.disabled || (!this._settings.allowUnSelectAll && isAllItemsSelected)) {
      return false;
    }
    if (!isAllItemsSelected) {
      this.selectedItems = this._data.filter((item) => !item.isDisabled).slice();
      this.selectAll.emit(this.emittedValue(this.selectedItems));
    } else {
      this.selectedItems = [];
      this.deSelectAll.emit(this.emittedValue(this.selectedItems));
    }
    this.onChangeCallback(this.emittedValue(this.selectedItems));
  }

  getFields(inputData) {
    const fields = [];
    if (typeof inputData !== 'object') {
      return fields;
    }
    // eslint-disable-next-line guard-for-in
    for (const prop in inputData) {
      fields.push(prop);
    }
    return fields;
  }

  toLowerCaseString(value: string | number): string {
    if (typeof value === 'number') {
      return value.toString().toLowerCase();
    }
    return value.toLowerCase();
  }
}
